import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import './LoginModal.css';

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

type AuthMode = 'login' | 'register' | 'forgotPassword';
type RegisterStep = 'form' | 'otp';
type ForgotPasswordStep = 'email' | 'otp' | 'newPassword';

const LoginModal: FC<LoginModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const { login, sendOtp, register, loginWithGoogle, sendPasswordResetOtp, resetPassword, error, clearError, isLoading } = useAuth();

  const [mode, setMode] = useState<AuthMode>('login');
  const [registerStep, setRegisterStep] = useState<RegisterStep>('form');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [otp, setOtp] = useState('');
  const [localError, setLocalError] = useState('');
  const [forgotPasswordStep, setForgotPasswordStep] = useState<ForgotPasswordStep>('email');
  const [newPassword, setNewPassword] = useState('');

  if (!isOpen) return null;

  const handleRegisterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError('');
    clearError();

    if (!email) {
      setLocalError(t('auth.emailRequired'));
      return;
    }

    if (!password) {
      setLocalError(t('auth.emailPasswordRequired'));
      return;
    }

    if (password.length < 6) {
      setLocalError(t('auth.passwordTooShort'));
      return;
    }

    // Advance to OTP screen immediately, send OTP in background
    setRegisterStep('otp');
    try {
      await sendOtp(email);
    } catch {
      // Error will show on OTP screen, user can resend
    }
  };

  const handleVerifyAndRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError('');
    clearError();

    if (!otp || otp.length !== 6) {
      setLocalError(t('auth.otpInvalid'));
      return;
    }

    try {
      await register(email, password, otp, displayName || undefined);
      onSuccess?.();
    } catch {
      // Error is handled by AuthContext
    }
  };

  const handleResendOtp = async () => {
    setLocalError('');
    clearError();
    try {
      await sendOtp(email);
    } catch {
      // Error is handled by AuthContext
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError('');
    clearError();

    if (!email || !password) {
      setLocalError(t('auth.emailPasswordRequired'));
      return;
    }

    try {
      await login(email, password);
      onSuccess?.();
    } catch {
      // Error is handled by AuthContext
    }
  };

  const handleGoogleLogin = async () => {
    setLocalError('');
    clearError();
    try {
      await loginWithGoogle();
    } catch {
      // Error is handled by AuthContext
    }
  };

  const handleForgotPasswordSendOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError('');
    clearError();

    if (!email) {
      setLocalError(t('auth.emailRequired'));
      return;
    }

    try {
      await sendPasswordResetOtp(email);
      setForgotPasswordStep('otp');
    } catch {
      // Error is handled by AuthContext
    }
  };

  const handleForgotPasswordVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError('');
    clearError();

    if (!otp || otp.length !== 6) {
      setLocalError(t('auth.otpInvalid'));
      return;
    }

    setForgotPasswordStep('newPassword');
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError('');
    clearError();

    if (!newPassword) {
      setLocalError(t('auth.passwordRequired'));
      return;
    }

    if (newPassword.length < 6) {
      setLocalError(t('auth.passwordTooShort'));
      return;
    }

    try {
      await resetPassword(email, otp, newPassword);
      onSuccess?.();
    } catch {
      // Error is handled by AuthContext
    }
  };

  const toggleMode = () => {
    setMode(mode === 'login' ? 'register' : 'login');
    setRegisterStep('form');
    setForgotPasswordStep('email');
    setEmail('');
    setPassword('');
    setDisplayName('');
    setOtp('');
    setNewPassword('');
    setLocalError('');
    clearError();
  };

  const enterForgotPassword = () => {
    setMode('forgotPassword');
    setForgotPasswordStep('email');
    setOtp('');
    setNewPassword('');
    setLocalError('');
    clearError();
  };

  const backToLogin = () => {
    setMode('login');
    setForgotPasswordStep('email');
    setEmail('');
    setPassword('');
    setOtp('');
    setNewPassword('');
    setLocalError('');
    clearError();
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const renderRegisterForm = () => {
    if (registerStep === 'form') {
      return (
        <form onSubmit={handleRegisterSubmit}>
          <div className="form-group">
            <label htmlFor="reg-email">{t('auth.email')}</label>
            <input
              type="email"
              id="reg-email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('auth.emailPlaceholder')}
              required
              disabled={isLoading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="reg-displayName">{t('auth.displayName')}</label>
            <input
              type="text"
              id="reg-displayName"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder={t('auth.displayNamePlaceholder')}
              disabled={isLoading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="reg-password">{t('auth.password')}</label>
            <input
              type="password"
              id="reg-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t('auth.passwordPlaceholder')}
              required
              disabled={isLoading}
            />
          </div>

          {(error || localError) && (
            <div className="error-message">
              {localError || error}
            </div>
          )}

          <button type="submit" className="submit-button" disabled={isLoading}>
            {isLoading ? (
              <span className="spinner"></span>
            ) : (
              t('auth.createAccount')
            )}
          </button>
        </form>
      );
    }

    // registerStep === 'otp'
    return (
      <form onSubmit={handleVerifyAndRegister}>
        <p className="otp-info">{t('auth.otpSentTo', { email })}</p>
        <div className="form-group">
          <label htmlFor="reg-otp">{t('auth.verificationCode')}</label>
          <input
            type="text"
            id="reg-otp"
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="000000"
            maxLength={6}
            className="otp-input"
            required
            disabled={isLoading}
            autoFocus
          />
        </div>

        {(error || localError) && (
          <div className="error-message">
            {localError || error}
          </div>
        )}

        <button type="submit" className="submit-button" disabled={isLoading || otp.length !== 6}>
          {isLoading ? (
            <span className="spinner"></span>
          ) : (
            t('auth.verifyCode')
          )}
        </button>

        <button
          type="button"
          className="resend-button"
          onClick={handleResendOtp}
          disabled={isLoading}
        >
          {t('auth.resendCode')}
        </button>
      </form>
    );
  };

  const getRegisterTitle = () => {
    return registerStep === 'form' ? t('auth.createAccount') : t('auth.verifyEmail');
  };

  const renderForgotPasswordForm = () => {
    if (forgotPasswordStep === 'email') {
      return (
        <form onSubmit={handleForgotPasswordSendOtp}>
          <p className="otp-info">{t('auth.forgotPasswordDescription')}</p>
          <div className="form-group">
            <label htmlFor="email">{t('auth.email')}</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('auth.emailPlaceholder')}
              required
              disabled={isLoading}
            />
          </div>

          {(error || localError) && (
            <div className="error-message">
              {localError || error}
            </div>
          )}

          <button type="submit" className="submit-button" disabled={isLoading}>
            {isLoading ? (
              <span className="spinner"></span>
            ) : (
              t('auth.sendVerificationCode')
            )}
          </button>
        </form>
      );
    }

    if (forgotPasswordStep === 'otp') {
      return (
        <form onSubmit={handleForgotPasswordVerifyOtp}>
          <p className="otp-info">{t('auth.otpSentTo', { email })}</p>
          <div className="form-group">
            <label htmlFor="otp">{t('auth.verificationCode')}</label>
            <input
              type="text"
              id="otp"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              maxLength={6}
              className="otp-input"
              required
              disabled={isLoading}
              autoFocus
            />
          </div>

          {(error || localError) && (
            <div className="error-message">
              {localError || error}
            </div>
          )}

          <button type="submit" className="submit-button" disabled={isLoading || otp.length !== 6}>
            {isLoading ? (
              <span className="spinner"></span>
            ) : (
              t('auth.verifyCode')
            )}
          </button>

          <button
            type="button"
            className="resend-button"
            onClick={handleForgotPasswordSendOtp as any}
            disabled={isLoading}
          >
            {t('auth.resendCode')}
          </button>
        </form>
      );
    }

    // forgotPasswordStep === 'newPassword'
    return (
      <form onSubmit={handleResetPassword}>
        <div className="form-group">
          <label htmlFor="newPassword">{t('auth.newPassword')}</label>
          <input
            type="password"
            id="newPassword"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder={t('auth.newPasswordPlaceholder')}
            required
            disabled={isLoading}
          />
        </div>

        {(error || localError) && (
          <div className="error-message">
            {localError || error}
          </div>
        )}

        <button type="submit" className="submit-button" disabled={isLoading}>
          {isLoading ? (
            <span className="spinner"></span>
          ) : (
            t('auth.resetPassword')
          )}
        </button>
      </form>
    );
  };

  const getForgotPasswordTitle = () => {
    switch (forgotPasswordStep) {
      case 'email': return t('auth.forgotPassword');
      case 'otp': return t('auth.verifyEmail');
      case 'newPassword': return t('auth.setNewPassword');
    }
  };

  return (
    <div className="login-modal-overlay" onClick={handleOverlayClick}>
      <div className="login-modal">
        <button className="close-button" onClick={onClose} aria-label="Close">
          &times;
        </button>

        <h2>
          {mode === 'login' && t('auth.signIn')}
          {mode === 'register' && getRegisterTitle()}
          {mode === 'forgotPassword' && getForgotPasswordTitle()}
        </h2>

        {(mode === 'login' || (mode === 'register' && registerStep === 'form')) && (
          <>
            <button
              className="google-button"
              onClick={handleGoogleLogin}
              disabled={isLoading}
            >
              <svg viewBox="0 0 24 24" width="20" height="20">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              {t('auth.continueWithGoogle')}
            </button>

            <div className="divider">
              <span>{t('auth.or')}</span>
            </div>
          </>
        )}

        {mode === 'login' ? (
          <>
            <form onSubmit={handleLogin}>
              <div className="form-group">
                <label htmlFor="email">{t('auth.email')}</label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('auth.emailPlaceholder')}
                  required
                  disabled={isLoading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="password">{t('auth.password')}</label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('auth.passwordPlaceholder')}
                  required
                  disabled={isLoading}
                />
              </div>

              {(error || localError) && (
                <div className="error-message">
                  {localError || error}
                </div>
              )}

              <button type="submit" className="submit-button" disabled={isLoading}>
                {isLoading ? (
                  <span className="spinner"></span>
                ) : (
                  t('auth.signIn')
                )}
              </button>
            </form>
            <button
              type="button"
              className="forgot-password-button"
              onClick={enterForgotPassword}
            >
              {t('auth.forgotPassword')}
            </button>
          </>
        ) : mode === 'register' ? (
          renderRegisterForm()
        ) : (
          renderForgotPasswordForm()
        )}

        <div className="switch-mode">
          {mode === 'login' ? (
            <p>
              {t('auth.noAccount')}{' '}
              <button type="button" onClick={toggleMode}>
                {t('auth.signUp')}
              </button>
            </p>
          ) : mode === 'register' ? (
            <p>
              {t('auth.haveAccount')}{' '}
              <button type="button" onClick={toggleMode}>
                {t('auth.signIn')}
              </button>
            </p>
          ) : (
            <p>
              <button type="button" onClick={backToLogin}>
                {t('auth.backToSignIn')}
              </button>
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginModal;
